import { Device } from '@twilio/voice-sdk';
import VoiceAPI from './voiceAPIClient';

const createCallDisconnectedEvent = () => new CustomEvent('call:disconnected');
const createIncomingCallEvent = callData =>
  new CustomEvent('call:incoming', { detail: callData });

class TwilioVoiceClient extends EventTarget {
  constructor() {
    super();
    this.device = null;
    this.activeConnection = null;
    this.initialized = false;
    this.inboxId = null;
    // Store Twilio Call objects outside of Vue reactivity to avoid proxy issues
    this.pendingCalls = new Map();
  }

  async initializeDevice(inboxId) {
    this.destroyDevice();

    const response = await VoiceAPI.getToken(inboxId);
    const { token, account_id, identity } = response || {};
    if (!token) throw new Error('Invalid token');

    this.device = new Device(token, {
      allowIncomingWhileBusy: true,
      disableAudioContextSounds: true,
      codecPreferences: ['opus', 'pcmu'],
      enableRingingState: true,
      appParams: { account_id },
    });

    this.device.removeAllListeners();

    // Handle outgoing call connections
    this.device.on('connect', conn => {
      this.activeConnection = conn;
      conn.on('disconnect', this.onDisconnect);
    });

    // Handle incoming calls (OSS direct dial)
    this.device.on('incoming', call => {
      const callSid = call.parameters.CallSid;
      // Store the Twilio Call object outside of Vue reactivity
      this.pendingCalls.set(callSid, call);

      this.dispatchEvent(
        createIncomingCallEvent({
          callSid,
          from: call.parameters.From,
          to: call.parameters.To,
          // Don't pass the call object - it will be retrieved from pendingCalls
        })
      );
    });

    this.device.on('disconnect', this.onDisconnect);

    this.device.on('tokenWillExpire', async () => {
      const r = await VoiceAPI.getToken(this.inboxId);
      if (r?.token) this.device.updateToken(r.token);
    });

    // Register device to receive incoming calls
    await this.device.register();

    this.initialized = true;
    this.inboxId = inboxId;

    return this.device;
  }

  get hasActiveConnection() {
    return !!this.activeConnection;
  }

  // Accept incoming call by callSid (OSS direct dial)
  acceptIncomingCall(callSid) {
    const call = this.pendingCalls.get(callSid);

    if (!call) {
      // eslint-disable-next-line no-console
      console.error('[TwilioVoice] acceptIncomingCall: No call found for callSid:', callSid);
      return null;
    }

    // eslint-disable-next-line no-console
    console.log('[TwilioVoice] Accepting incoming call:', callSid);

    try {
      call.accept();
      this.activeConnection = call;
      this.pendingCalls.delete(callSid);
      call.on('disconnect', this.onDisconnect);
      // eslint-disable-next-line no-console
      console.log('[TwilioVoice] Call accepted successfully');
      return call;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[TwilioVoice] Error accepting call:', error);
      return null;
    }
  }

  // Reject incoming call by callSid
  rejectIncomingCall(callSid) {
    const call = this.pendingCalls.get(callSid);
    if (call) {
      call.reject();
      this.pendingCalls.delete(callSid);
    }
  }

  // Mute/unmute the current call
  setMute(muted) {
    if (this.activeConnection) {
      this.activeConnection.mute(muted);
    }
  }

  // Send DTMF tones
  sendDigits(digits) {
    if (this.activeConnection) {
      this.activeConnection.sendDigits(digits);
    }
  }

  endClientCall() {
    if (this.activeConnection) {
      this.activeConnection.disconnect();
    }
    this.activeConnection = null;
    if (this.device) {
      this.device.disconnectAll();
    }
  }

  destroyDevice() {
    if (this.device) {
      this.device.unregister();
      this.device.destroy();
    }
    this.activeConnection = null;
    this.device = null;
    this.initialized = false;
    this.inboxId = null;
    this.pendingCalls.clear();
  }

  // Conference-based call (Enterprise) - join existing conference
  async joinClientCall({ to, conversationId }) {
    if (!this.device || !this.initialized || !to) return null;
    if (this.activeConnection) return this.activeConnection;

    const params = {
      To: to,
      is_agent: 'true',
      conversation_id: conversationId,
    };

    const connection = await this.device.connect({ params });
    this.activeConnection = connection;

    connection.on('disconnect', this.onDisconnect);

    return connection;
  }

  // Initiate outbound call directly via Device.connect() (OSS)
  async makeOutboundCall({ phoneNumber, contactId, conversationId }) {
    if (!this.device || !this.initialized) {
      throw new Error('Device not initialized');
    }
    if (this.activeConnection) {
      throw new Error('Already have an active call');
    }
    if (!phoneNumber) {
      throw new Error('Phone number is required');
    }

    const params = {
      To: phoneNumber,
      contact_id: contactId || '',
      conversation_id: conversationId || '',
    };

    // eslint-disable-next-line no-console
    console.log('[TwilioVoice] Making outbound call to:', phoneNumber);

    const connection = await this.device.connect({ params });
    this.activeConnection = connection;

    connection.on('disconnect', this.onDisconnect);

    // eslint-disable-next-line no-console
    console.log('[TwilioVoice] Outbound call connected');

    return connection;
  }

  onDisconnect = () => {
    this.activeConnection = null;
    this.dispatchEvent(createCallDisconnectedEvent());
  };
}

export default new TwilioVoiceClient();
